import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { addPettyCount, addPettyEntry, getMyPettyCash } from "@/lib/api";
import { FilePick } from "@/components/accounts/RequestQueue";
import { Empty, Stat, TH, TR, errMsg, money } from "@/components/accounts/shared";

const CATS = ["Supplies", "Maintenance", "Marketing", "Equipment", "Utilities", "Other"];
const today = () => new Date().toISOString().slice(0, 10);

// The gym's petty cash: a fixed float (set by accounts), spends with receipts, top-ups, a weekly count.
export default function PettyCash() {
  const [s, setS] = useState(null);
  const [f, setF] = useState({ kind: "spend", date: today(), amount: "", category: "Supplies", note: "", receipt_id: null });
  const [count, setCount] = useState({ date: today(), counted: "", note: "" });
  const [fileKey, setFileKey] = useState(0);
  const load = useCallback(() => getMyPettyCash().then(setS).catch((e) => toast.error(errMsg(e, "Could not load petty cash"))), []);
  useEffect(() => { load(); }, [load]);
  if (!s) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const add = async () => {
    try {
      setS(await addPettyEntry({ ...f, amount: Number(f.amount) }));
      setF({ ...f, amount: "", note: "", receipt_id: null });
      setFileKey((k) => k + 1);
      toast.success(f.kind === "spend" ? "Spend recorded (and added to Expenses)" : "Top-up recorded");
    } catch (e) { toast.error(errMsg(e, "Could not save")); }
  };
  const doCount = async () => {
    try { setS(await addPettyCount({ ...count, counted: Number(count.counted) })); setCount({ ...count, counted: "", note: "" }); toast.success("Count saved"); }
    catch (e) { toast.error(errMsg(e, "Could not save")); }
  };
  return (
    <div className="space-y-5 max-w-4xl" data-testid="petty-page">
      <div>
        <div className="eyebrow">This gym</div>
        <h1 className="text-2xl font-semibold text-zinc-50">Petty cash</h1>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Stat label="Float" value={s.float_amount ? money(s, s.float_amount) : "Not set"} sub="set by accounts" />
        <Stat label="Should be in the box" value={money(s, s.expected)} tone={s.low ? "bad" : undefined} sub={s.low ? "Low: top up" : "float + top-ups − spends"} />
        <Stat label="Weekly count" value={s.count_due ? "Due" : "Done"} tone={s.count_due ? "bad" : "good"} sub={s.counts[0] ? `last ${s.counts[0].date}` : "never counted"} />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card-surface p-4 space-y-2">
          <div className="flex gap-1.5">{[["spend", "Spend"], ["topup", "Top-up"]].map(([k, l]) => (
            <button key={k} onClick={() => setF({ ...f, kind: k })} className={`text-xs px-3 py-1 rounded-full border ${f.kind === k ? "border-blue-500/60 bg-blue-500/10 text-blue-200" : "border-[#262626] text-zinc-400"}`}>{l}</button>
          ))}</div>
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="bg-[#0a0a0a] border-[#262626]" />
            <Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Amount" className="bg-[#0a0a0a] border-[#262626]" data-testid="petty-amount" />
          </div>
          {f.kind === "spend" && <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="w-full bg-[#0a0a0a] border border-[#262626] rounded-md h-9 px-2 text-sm">{CATS.map((c) => <option key={c}>{c}</option>)}</select>}
          <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder={f.kind === "spend" ? "What for" : "Note"} className="bg-[#0a0a0a] border-[#262626]" />
          {f.kind === "spend" && <FilePick key={fileKey} onUploaded={(id) => setF({ ...f, receipt_id: id })} label="Receipt photo" />}
          <Button size="sm" onClick={add} disabled={!Number(f.amount)} className="bg-blue-600 hover:bg-blue-500" data-testid="petty-save">Save</Button>
        </div>
        <div className="card-surface p-4 space-y-2">
          <div className="text-sm font-semibold text-zinc-100">Weekly count</div>
          <div className="text-[11px] text-zinc-500">Count the cash in the box. Expected: {money(s, s.expected)}.</div>
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={count.date} onChange={(e) => setCount({ ...count, date: e.target.value })} className="bg-[#0a0a0a] border-[#262626]" />
            <Input type="number" value={count.counted} onChange={(e) => setCount({ ...count, counted: e.target.value })} placeholder="Counted" className="bg-[#0a0a0a] border-[#262626]" />
          </div>
          {count.counted !== "" && Math.abs(Number(count.counted) - s.expected) >= 1 && (
            <Input value={count.note} onChange={(e) => setCount({ ...count, note: e.target.value })} placeholder={`Off by ${money(s, Number(count.counted) - s.expected)}: why?`} className="bg-[#0a0a0a] border-[#262626]" />
          )}
          <Button size="sm" onClick={doCount} disabled={count.counted === ""} className="bg-blue-600 hover:bg-blue-500">Save count</Button>
        </div>
      </div>
      <div className="card-surface overflow-x-auto">
        {s.txns.length === 0 ? <Empty>No spends or top-ups yet.</Empty> : (
          <table className="w-full text-sm"><thead><tr className={TH}><th className="px-3 py-2 font-medium">Date</th><th className="px-3 py-2 font-medium">What</th>
            <th className="px-3 py-2 font-medium text-right">Amount</th><th className="px-3 py-2 font-medium">By</th></tr></thead>
            <tbody>{s.txns.map((t) => (
              <tr key={t.id} className={TR}><td className="px-3 py-2 tabular-nums text-zinc-400">{t.date}</td>
                <td className="px-3 py-2 text-zinc-300">{t.kind === "spend" ? t.category : "Top-up"}<div className="text-[10px] text-zinc-500">{t.note}{t.receipt_id ? " · receipt" : ""}</div></td>
                <td className={`px-3 py-2 text-right tabular-nums ${t.kind === "spend" ? "text-rose-200" : "text-emerald-200"}`}>{t.kind === "spend" ? "−" : "+"}{money(s, t.amount)}</td>
                <td className="px-3 py-2 text-xs text-zinc-500">{t.by}</td></tr>))}</tbody></table>
        )}
      </div>
    </div>
  );
}
